/**
 * AdminDeployPreflight — Owner-only mandatory pre-deploy gate.
 *
 * Runs 8 checks against the current environment and shows a single
 * PASS/FAIL verdict + downloadable text report.
 */
import { useState } from "react";
import { CheckCircle2, XCircle, Loader2, Rocket, Download, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import usePageMeta from "@/hooks/usePageMeta";

const CHECK_LABELS = {
  regression_suite: "Production Regression Suite",
  revenue_snapshot: "Revenue Snapshot",
  stripe_connectivity: "Stripe Connectivity",
  resend_connectivity: "Resend Connectivity",
  env_validation: "Environment Validation",
  feature_flags: "Feature Flag Summary",
  database_connectivity: "Database Connectivity",
  migration_status: "Migration / Index Status",
};

export default function AdminDeployPreflight() {
  usePageMeta({ title: "Deploy Preflight · Admin", path: "/admin/deploy-preflight" });
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const isOwner = Boolean(user?.is_owner);

  async function run() {
    setRunning(true);
    setError(null);
    setReport(null);
    try {
      const r = await api.get("/admin/deploy-preflight", { timeout: 240000 });
      setReport(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Preflight failed");
    } finally {
      setRunning(false);
    }
  }

  async function downloadReport() {
    try {
      const r = await api.get("/admin/deploy-preflight?format=text", { timeout: 240000 });
      const blob = new Blob([r.data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `colourmatch-preflight-${new Date().toISOString().slice(0, 19)}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.message || "Download failed");
    }
  }

  if (!isOwner) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="preflight-owner-only">
        <div className="border border-rose-200 bg-rose-50 p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-rose-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-rose-900">Owner-only</div>
            <div className="text-sm text-rose-800 mt-1">
              The Deploy Preflight is restricted to the Owner account. This is a mandatory
              gate before every production deployment.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-8" data-testid="deploy-preflight-page">
      <div>
        <div className="overline text-brand-teal mb-1">Owner tools</div>
        <h1 className="font-serif text-3xl text-brand-ink flex items-center gap-3">
          <Rocket className="w-7 h-7 text-brand-teal" />
          Deploy Preflight
        </h1>
        <p className="text-sm text-brand-muted mt-2 max-w-2xl">
          Mandatory gate before every production deployment. Runs the production regression
          suite, snapshots revenue, and verifies Stripe, Resend, environment variables,
          feature flags, database connectivity and migration status. Deployment is blocked
          on any 🔴 FAIL.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={run}
          disabled={running}
          data-testid="preflight-run-btn"
          className="inline-flex items-center gap-2 px-6 h-11 bg-brand-navy text-white text-sm hover:bg-brand-navy/90 disabled:opacity-60 transition-colors"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
          {running ? "Running preflight…" : "Run preflight"}
        </button>
        {report && (
          <button
            onClick={downloadReport}
            data-testid="preflight-download-btn"
            className="inline-flex items-center gap-2 px-4 h-11 bg-white border border-brand-linen hover:border-brand-teal text-sm transition-colors"
          >
            <Download className="w-4 h-4" /> Download report
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 border border-rose-200 bg-rose-50 text-sm text-rose-900" data-testid="preflight-error">
          {error}
        </div>
      )}

      {report && (
        <div className="space-y-6" data-testid="preflight-report">
          {/* Overall verdict */}
          <div
            className={`p-8 text-center border ${
              report.status === "PASS"
                ? "border-emerald-300 bg-emerald-50"
                : "border-rose-300 bg-rose-50"
            }`}
            data-testid={`preflight-verdict-${report.status.toLowerCase()}`}
          >
            {report.status === "PASS" ? (
              <>
                <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-3" />
                <div className="font-serif text-4xl text-emerald-900">🟢 PASS</div>
                <div className="text-sm text-emerald-800 mt-2">
                  All {report.checks?.length || 0} checks passed in {report.duration_ms}ms.
                  Deployment is cleared to proceed.
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 text-rose-600 mx-auto mb-3" />
                <div className="font-serif text-4xl text-rose-900">🔴 FAIL</div>
                <div className="text-sm text-rose-800 mt-2">
                  {report.checks?.filter((c) => !c.ok).length || 0} check(s) failed.
                  Deployment is BLOCKED until all failures are resolved.
                </div>
              </>
            )}
          </div>

          {/* Per-check breakdown */}
          <div className="space-y-2">
            {report.checks?.map((c) => (
              <div
                key={c.check}
                className={`border p-4 flex items-start gap-3 ${
                  c.ok ? "border-emerald-200 bg-white" : "border-rose-300 bg-rose-50"
                }`}
                data-testid={`preflight-check-${c.check}`}
              >
                {c.ok ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium text-sm text-brand-ink">
                      {CHECK_LABELS[c.check] || c.check}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wide font-medium ${
                      c.ok ? "text-emerald-700" : "text-rose-700"
                    }`}>
                      {c.status}
                    </span>
                  </div>
                  {c.error && (
                    <div className="text-xs text-rose-800 mt-1 font-mono break-words">
                      {c.error}
                    </div>
                  )}
                  {c.details && Object.keys(c.details).length > 0 && (
                    <details className="text-xs text-brand-muted mt-2">
                      <summary className="cursor-pointer hover:text-brand-ink">Details</summary>
                      <pre className="mt-2 p-3 bg-stone-50 border border-brand-linen overflow-x-auto text-[11px]">
                        {JSON.stringify(c.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="text-xs text-brand-muted">
            Run at {new Date(report.checked_at).toLocaleString("en-AU")} · Duration {report.duration_ms}ms
          </div>
        </div>
      )}
    </div>
  );
}
